import { useEffect, useState } from "react";
import {
  CircleNotch,
  FileText,
  Sparkle,
  Coins,
  Package,
  CloudArrowUp,
  Buildings,
  Factory,
  ShoppingCart,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import api from "@/lib/api";

const METALS = ["Gold", "Silver", "Platinum"];

const PURITIES = {
  Gold: ["24K", "22K", "20K", "18K", "14K", "9K"],
  Silver: ["999 Silver", "925 Silver", "800 Silver", "750 Silver", "650 Silver", "500 Silver"],
  Platinum: ["950 Platinum"],
};

const CITIES = ["chennai", "mumbai", "delhi", "bangalore", "hyderabad", "kolkata"];

const AUDIENCES = [
  { id: "retailer", label: "Retailer", icon: ShoppingCart, hint: "B2B retail markup focus" },
  { id: "manufacturer", label: "Manufacturer", icon: Factory, hint: "Production specs + lead time" },
  { id: "wholesale", label: "Wholesale", icon: Buildings, hint: "Bulk tier economics" },
];

const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

export default function AdminQuotes() {
  const [status, setStatus] = useState(null);
  const [form, setForm] = useState({
    product_name: "",
    metal: "Gold",
    purity: "22K",
    weight_grams: "",
    making_charge_percent: "12",
    stones_value_inr: "0",
    city: "chennai",
    audience: "retailer",
    notes: "",
  });
  const [generating, setGenerating] = useState(false);
  const [savingDoc, setSavingDoc] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    api.get("/admin/quotes/status").then((r) => setStatus(r.data)).catch(() => {});
  }, []);

  const setField = (k, v) => setForm({ ...form, [k]: v });

  // Reset purity when metal changes
  useEffect(() => {
    if (!PURITIES[form.metal]?.includes(form.purity)) {
      setForm((f) => ({ ...f, purity: PURITIES[form.metal][0] }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.metal]);

  const generate = async (e) => {
    e?.preventDefault();
    if (!form.product_name.trim() || !form.weight_grams) {
      toast.error("Fill product name and weight");
      return;
    }
    setGenerating(true);
    setResult(null);
    try {
      const r = await api.post("/admin/quotes/generate", {
        ...form,
        weight_grams: Number(form.weight_grams),
        making_charge_percent: Number(form.making_charge_percent),
        stones_value_inr: Number(form.stones_value_inr || 0),
      });
      setResult(r.data);
      toast.success("Quote generated by GPT-4o");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Quote generation failed");
    } finally {
      setGenerating(false);
    }
  };

  const saveAsDocument = async () => {
    if (!result) return;
    setSavingDoc(true);
    try {
      const blob = new Blob([result.quote_markdown], { type: "text/markdown" });
      const filename = `${form.product_name.replace(/\s+/g, "_")}_${form.audience}_${Date.now()}.md`;
      const fd = new FormData();
      fd.append("file", blob, filename);
      fd.append("label", `Quote: ${form.product_name} (${form.audience})`);
      fd.append("category", "general");
      fd.append("is_public", "false");
      await api.post("/admin/documents", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Saved to Documents");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save");
    } finally {
      setSavingDoc(false);
    }
  };

  const ready = status?.quote_generator_ready;
  const purityOptions = PURITIES[form.metal] || [];

  return (
    <div data-testid="admin-quotes-page">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <p className="text-xs uppercase tracking-[0.25em] text-[#3B5BDB] font-semibold flex items-center gap-2">
            <Sparkle size={14} weight="fill" className="text-[#D4AF37]" /> B2B / Manufacturing
          </p>
          <h1 className="font-display text-3xl sm:text-4xl mt-2 text-[#0A1128]">
            Quote Generator
          </h1>
          <p className="mt-2 text-sm text-[#4A5568] max-w-2xl">
            GPT-4o drafts a professional quote in Markdown for retailers, manufacturers, or
            wholesale buyers. Pricing math is computed deterministically from your live{" "}
            <code className="px-1.5 py-0.5 bg-[#F5EFE6] rounded text-xs">/admin/board-rates</code>.
          </p>
        </div>
        {status && (
          <span
            className={`text-[11px] tracking-wider uppercase font-semibold px-2.5 py-1 rounded-full ${
              ready
                ? "bg-[#16A34A]/15 text-[#16A34A]"
                : "bg-[#D4AF37]/15 text-[#8a6d1c]"
            }`}
            data-testid="quotes-status-pill"
          >
            {ready ? "GPT-4o · OpenRouter live" : "OpenRouter key missing"}
          </span>
        )}
      </div>

      {!ready && (
        <div className="mt-5 mc-card p-4 border-l-4 border-[#D4AF37]" data-testid="quotes-key-warning">
          <p className="text-sm text-[#0A1128]">
            <strong>Quote Generator needs OpenRouter.</strong> Set{" "}
            <code className="px-1 py-0.5 bg-[#F5EFE6] rounded">OPENROUTER_API_KEY</code> in{" "}
            <code className="px-1 py-0.5 bg-[#F5EFE6] rounded">backend/.env</code>, restart the
            backend, and you can generate quotes with GPT-4o (
            <code className="text-xs">{status?.models?.gpt4o}</code>) immediately.
          </p>
        </div>
      )}

      <div className="mt-6 grid lg:grid-cols-2 gap-6">
        {/* Form */}
        <form
          onSubmit={generate}
          className="mc-card p-6 space-y-4"
          data-testid="quote-form"
        >
          <div className="flex items-center gap-2">
            <Package size={20} weight="duotone" className="text-[#3B5BDB]" />
            <h2 className="font-display text-xl text-[#0A1128]">Product spec</h2>
          </div>

          <div>
            <label className="text-[11px] uppercase tracking-wider text-[#4A5568]">Product name *</label>
            <input
              data-testid="quote-product-name"
              type="text"
              required
              value={form.product_name}
              onChange={(e) => setField("product_name", e.target.value)}
              placeholder="22K Antique Long Necklace"
              className="mt-1 w-full h-11 px-3 rounded-md border border-[#E8DFD1] bg-white outline-none focus:border-[#3B5BDB]"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[11px] uppercase tracking-wider text-[#4A5568]">Metal</label>
              <select
                data-testid="quote-metal"
                value={form.metal}
                onChange={(e) => setField("metal", e.target.value)}
                className="mt-1 w-full h-11 px-3 rounded-md border border-[#E8DFD1] bg-white outline-none focus:border-[#3B5BDB]"
              >
                {METALS.map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wider text-[#4A5568]">Purity</label>
              <select
                data-testid="quote-purity"
                value={form.purity}
                onChange={(e) => setField("purity", e.target.value)}
                className="mt-1 w-full h-11 px-3 rounded-md border border-[#E8DFD1] bg-white outline-none focus:border-[#3B5BDB]"
              >
                {purityOptions.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-[11px] uppercase tracking-wider text-[#4A5568]">Weight (g) *</label>
              <input
                data-testid="quote-weight"
                type="number"
                required
                min="0.01"
                step="0.01"
                value={form.weight_grams}
                onChange={(e) => setField("weight_grams", e.target.value)}
                placeholder="25.5"
                className="mt-1 w-full h-11 px-3 rounded-md border border-[#E8DFD1] bg-white outline-none focus:border-[#3B5BDB]"
              />
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wider text-[#4A5568]">Making %</label>
              <input
                data-testid="quote-making"
                type="number"
                min="0"
                max="200"
                step="0.5"
                value={form.making_charge_percent}
                onChange={(e) => setField("making_charge_percent", e.target.value)}
                className="mt-1 w-full h-11 px-3 rounded-md border border-[#E8DFD1] bg-white outline-none focus:border-[#3B5BDB]"
              />
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wider text-[#4A5568]">Stones (₹)</label>
              <input
                data-testid="quote-stones"
                type="number"
                min="0"
                value={form.stones_value_inr}
                onChange={(e) => setField("stones_value_inr", e.target.value)}
                className="mt-1 w-full h-11 px-3 rounded-md border border-[#E8DFD1] bg-white outline-none focus:border-[#3B5BDB]"
              />
            </div>
          </div>

          <div>
            <label className="text-[11px] uppercase tracking-wider text-[#4A5568]">Board rate city</label>
            <select
              data-testid="quote-city"
              value={form.city}
              onChange={(e) => setField("city", e.target.value)}
              className="mt-1 w-full h-11 px-3 rounded-md border border-[#E8DFD1] bg-white outline-none focus:border-[#3B5BDB]"
            >
              {CITIES.map((c) => <option key={c} value={c}>{c[0].toUpperCase() + c.slice(1)}</option>)}
            </select>
          </div>

          <div>
            <p className="text-[11px] uppercase tracking-wider text-[#4A5568]">Audience</p>
            <div className="mt-2 grid grid-cols-3 gap-2">
              {AUDIENCES.map((a) => {
                const Icon = a.icon;
                const active = form.audience === a.id;
                return (
                  <button
                    key={a.id}
                    type="button"
                    onClick={() => setField("audience", a.id)}
                    data-testid={`quote-audience-${a.id}`}
                    className={`p-3 rounded-md border text-left smooth ${
                      active
                        ? "border-[#3B5BDB] bg-[#3B5BDB]/5 ring-1 ring-[#3B5BDB]"
                        : "border-[#E8DFD1] hover:border-[#3B5BDB]/40"
                    }`}
                  >
                    <Icon size={18} weight="duotone" className={active ? "text-[#3B5BDB]" : "text-[#4A5568]"} />
                    <p className="mt-1 text-sm font-medium text-[#0A1128]">{a.label}</p>
                    <p className="text-[10px] text-[#4A5568] leading-tight">{a.hint}</p>
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <label className="text-[11px] uppercase tracking-wider text-[#4A5568]">Notes (optional)</label>
            <textarea
              data-testid="quote-notes"
              value={form.notes}
              onChange={(e) => setField("notes", e.target.value)}
              rows={3}
              placeholder="Hallmark required. Lead time: 14 days. Antique finish."
              className="mt-1 w-full px-3 py-2 rounded-md border border-[#E8DFD1] bg-white outline-none focus:border-[#3B5BDB] resize-none"
            />
          </div>

          <button
            type="submit"
            data-testid="quote-generate-btn"
            disabled={generating || !ready}
            className="w-full h-11 rounded-md bg-[#3B5BDB] text-white font-medium hover:bg-[#2F49AF] smooth disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {generating ? <CircleNotch size={16} className="animate-spin" weight="bold" /> : <Sparkle size={16} weight="fill" />}
            {generating ? "Generating..." : "Generate quote with GPT-4o"}
          </button>
        </form>

        {/* Result */}
        <div className="mc-card p-6" data-testid="quote-result-card">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="flex items-center gap-2">
              <FileText size={20} weight="duotone" className="text-[#3B5BDB]" />
              <h2 className="font-display text-xl text-[#0A1128]">Quote</h2>
            </div>
            {result && (
              <button
                onClick={saveAsDocument}
                disabled={savingDoc}
                data-testid="quote-save-doc-btn"
                className="px-3 py-1.5 rounded-md text-xs font-medium border border-[#3B5BDB] text-[#3B5BDB] hover:bg-[#3B5BDB] hover:text-white smooth flex items-center gap-1.5 disabled:opacity-40"
              >
                {savingDoc ? <CircleNotch size={12} className="animate-spin" weight="bold" /> : <CloudArrowUp size={12} weight="duotone" />}
                Save to Documents
              </button>
            )}
          </div>

          {!result ? (
            <div className="mt-6 text-center py-12">
              <Coins size={36} weight="duotone" className="text-[#3B5BDB]/30 mx-auto" />
              <p className="mt-3 font-display text-lg text-[#0A1128]">No quote yet</p>
              <p className="mt-1 text-xs text-[#4A5568]">Fill the form and hit "Generate quote".</p>
            </div>
          ) : (
            <div className="mt-4">
              {/* Numbers card */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5" data-testid="quote-numbers">
                <Stat label="Per gram" value={inr(result.numbers.rate_per_gram_inr)} />
                <Stat label="Melt" value={inr(result.numbers.melt_value_inr)} />
                <Stat label="Total" value={inr(result.numbers.total_inr)} accent />
                <Stat label="Suggested retail" value={inr(result.numbers.suggested_retail_inr)} />
              </div>
              <article
                className="prose prose-sm max-w-none prose-headings:font-display prose-headings:text-[#0A1128] prose-strong:text-[#0A1128] prose-p:text-[#4A5568] prose-table:text-sm"
                data-testid="quote-markdown"
              >
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {result.quote_markdown}
                </ReactMarkdown>
              </article>
              <p className="mt-5 text-[11px] text-[#4A5568]/80">
                Generated by <code>{result.model_used}</code> ·{" "}
                {new Date(result.generated_at).toLocaleString()}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, accent = false }) {
  return (
    <div className={`p-3 rounded-md ${accent ? "bg-[#3B5BDB]/8 border border-[#3B5BDB]/30" : "bg-[#F5EFE6]"}`}>
      <p className="text-[10px] uppercase tracking-wider text-[#4A5568]">{label}</p>
      <p className={`mt-1 font-display text-lg ${accent ? "text-[#3B5BDB]" : "text-[#0A1128]"} tabular-nums`}>{value}</p>
    </div>
  );
}
